import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, Truck, MapPin, Plus, Trash2, Save, Settings, 
  DollarSign, Package, Edit2, Check, X, AlertCircle, MessageCircle, Ban
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

function AdminDeliveryZones({ user, onLogout }) {
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || 'ru';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [settings, setSettings] = useState([]);
  const [zones, setZones] = useState([]);
  
  // Dialog states
  const [showZoneDialog, setShowZoneDialog] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [newZone, setNewZone] = useState({
    name: '',
    distance_from: 0,
    distance_to: 5,
    delivery_fee: 1000,
    priority: 0,
    delivery_disabled: false,
    no_delivery_message: '',
    whatsapp_link: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch cities
      const citiesRes = await axios.get(`${API}/cities`);
      setCities(citiesRes.data);
      
      // Fetch delivery settings
      const settingsRes = await axios.get(`${API}/delivery/admin/settings`, {
        headers: getAuthHeaders()
      });
      setSettings(settingsRes.data);
      
      // Select first city by default
      if (citiesRes.data.length > 0) {
        const firstCity = citiesRes.data[0];
        setSelectedCity(firstCity);
        await fetchZones(firstCity.id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async (cityId) => {
    try {
      const res = await axios.get(`${API}/delivery/admin/zones/${cityId}`, {
        headers: getAuthHeaders()
      });
      setZones(res.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
      setZones([]);
    }
  };

  const handleCitySelect = async (city) => {
    setSelectedCity(city);
    await fetchZones(city.id);
  };

  const handleSettingsChange = (cityId, field, value) => {
    setSettings(prev => prev.map(s => 
      s.city_id === cityId ? { ...s, [field]: value } : s
    ));
  };

  const saveSettings = async (cityId) => {
    const citySettings = settings.find(s => s.city_id === cityId);
    if (!citySettings) return;

    setSaving(true);
    try {
      await axios.post(`${API}/delivery/admin/settings`, {
        city_id: cityId,
        min_order_amount: parseFloat(citySettings.min_order_amount),
        free_delivery_threshold: parseFloat(citySettings.free_delivery_threshold),
        default_delivery_fee: parseFloat(citySettings.default_delivery_fee),
        is_enabled: citySettings.is_enabled
      }, { headers: getAuthHeaders() });
      
      toast.success('Настройки сохранены');
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const openAddZoneDialog = () => {
    setEditingZone(null);
    const lastZone = zones.length > 0 ? zones[zones.length - 1] : null;
    setNewZone({
      name: '',
      distance_from: lastZone ? lastZone.distance_to : 0,
      distance_to: lastZone ? lastZone.distance_to + 5 : 5,
      delivery_fee: 1000,
      priority: zones.length + 1,
      delivery_disabled: false,
      no_delivery_message: '',
      whatsapp_link: ''
    });
    setShowZoneDialog(true);
  };

  const openEditZoneDialog = (zone) => {
    setEditingZone(zone);
    setNewZone({
      name: zone.name,
      distance_from: zone.distance_from || 0,
      distance_to: zone.distance_to || 100,
      delivery_fee: zone.delivery_fee,
      priority: zone.priority,
      delivery_disabled: zone.delivery_disabled || false,
      no_delivery_message: zone.no_delivery_message || '',
      whatsapp_link: zone.whatsapp_link || ''
    });
    setShowZoneDialog(true);
  };

  const saveZone = async () => {
    if (!newZone.name) {
      toast.error('Введите название зоны');
      return;
    }
    
    if (newZone.distance_from >= newZone.distance_to) {
      toast.error('Значение "До" должно быть больше "От"');
      return;
    }

    setSaving(true);
    try {
      const zoneData = {
        name: newZone.name,
        distance_from: parseFloat(newZone.distance_from),
        distance_to: parseFloat(newZone.distance_to),
        delivery_fee: parseFloat(newZone.delivery_fee),
        priority: parseInt(newZone.priority),
        delivery_disabled: newZone.delivery_disabled,
        no_delivery_message: newZone.no_delivery_message || null,
        whatsapp_link: newZone.whatsapp_link || null
      };
      
      if (editingZone) {
        // Update existing zone
        await axios.put(`${API}/delivery/admin/zones/${editingZone.id}`, zoneData, { headers: getAuthHeaders() });
        toast.success('Зона обновлена');
      } else {
        // Create new zone
        await axios.post(`${API}/delivery/admin/zones`, {
          ...zoneData,
          city_id: selectedCity.id,
          is_active: true
        }, { headers: getAuthHeaders() });
        toast.success('Зона создана');
      }
      
      setShowZoneDialog(false);
      await fetchZones(selectedCity.id);
    } catch (error) {
      toast.error('Ошибка сохранения зоны');
    } finally {
      setSaving(false);
    }
  };

  const deleteZone = async (zoneId) => {
    if (!confirm('Удалить эту зону доставки?')) return;
    
    try {
      await axios.delete(`${API}/delivery/admin/zones/${zoneId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Зона удалена');
      await fetchZones(selectedCity.id);
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price) + ' ₸';
  };

  const currentSettings = settings.find(s => s.city_id === selectedCity?.id) || {
    min_order_amount: 10000,
    free_delivery_threshold: 15000,
    default_delivery_fee: 2000,
    is_enabled: true
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/${currentLang}/admin`)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Truck className="w-6 h-6 text-emerald-600" />
                  Настройки доставки
                </h1>
                <p className="text-sm text-gray-500">Управление зонами и ценами доставки</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* City Tabs */}
        <div className="flex gap-2 mb-6">
          {cities.map(city => (
            <Button
              key={city.id}
              variant={selectedCity?.id === city.id ? 'default' : 'outline'}
              onClick={() => handleCitySelect(city)}
              className={selectedCity?.id === city.id ? 'bg-emerald-700 hover:bg-emerald-800' : ''}
            >
              <MapPin className="w-4 h-4 mr-2" />
              {city.name}
            </Button>
          ))}
        </div>

        {selectedCity && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Настройки для {selectedCity.name}
                </CardTitle>
                <CardDescription>
                  Пороги заказа и стоимость доставки по умолчанию
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Доставка включена</p>
                    <p className="text-sm text-gray-500">Клиенты могут заказывать доставку</p>
                  </div>
                  <Switch
                    checked={currentSettings.is_enabled}
                    onCheckedChange={(checked) => handleSettingsChange(selectedCity.id, 'is_enabled', checked)}
                  />
                </div>

                {/* Min Order */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Минимальный заказ для доставки
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={currentSettings.min_order_amount}
                      onChange={(e) => handleSettingsChange(selectedCity.id, 'min_order_amount', e.target.value)}
                      className="max-w-[200px]"
                    />
                    <span className="text-gray-500">₸</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Заказы ниже этой суммы не смогут выбрать доставку
                  </p>
                </div>

                {/* Free Delivery Threshold */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Порог бесплатной доставки
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={currentSettings.free_delivery_threshold}
                      onChange={(e) => handleSettingsChange(selectedCity.id, 'free_delivery_threshold', e.target.value)}
                      className="max-w-[200px]"
                    />
                    <span className="text-gray-500">₸</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Заказы от этой суммы получают бесплатную доставку
                  </p>
                </div>

                {/* Default Fee */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Стоимость доставки по умолчанию
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={currentSettings.default_delivery_fee}
                      onChange={(e) => handleSettingsChange(selectedCity.id, 'default_delivery_fee', e.target.value)}
                      className="max-w-[200px]"
                    />
                    <span className="text-gray-500">₸</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Используется если район клиента не определён
                  </p>
                </div>

                <Button 
                  onClick={() => saveSettings(selectedCity.id)}
                  disabled={saving}
                  className="w-full bg-emerald-700 hover:bg-emerald-800"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Сохранение...' : 'Сохранить настройки'}
                </Button>
              </CardContent>
            </Card>

            {/* Zones Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Зоны доставки
                    </CardTitle>
                    <CardDescription>
                      Районы и стоимость доставки по зонам
                    </CardDescription>
                  </div>
                  <Button onClick={openAddZoneDialog} size="sm" className="bg-emerald-700 hover:bg-emerald-800">
                    <Plus className="w-4 h-4 mr-1" />
                    Добавить зону
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {zones.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Нет зон доставки</p>
                    <p className="text-sm">Добавьте зоны для настройки цен по расстоянию</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {zones.sort((a, b) => a.distance_from - b.distance_from).map((zone) => (
                      <div 
                        key={zone.id} 
                        className={`p-4 border rounded-lg transition-colors ${
                          zone.delivery_disabled 
                            ? 'bg-red-50 border-red-200' 
                            : 'hover:border-emerald-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              {zone.name}
                              <Badge variant="outline" className="text-xs">
                                {zone.distance_from}-{zone.distance_to} км
                              </Badge>
                              {zone.delivery_disabled && (
                                <Badge variant="destructive" className="text-xs">
                                  <Ban className="w-3 h-3 mr-1" />
                                  Отключена
                                </Badge>
                              )}
                            </h4>
                            {zone.delivery_disabled ? (
                              <p className="text-sm text-red-600 mt-1">
                                {zone.no_delivery_message || 'Доставка временно недоступна'}
                              </p>
                            ) : (
                              <p className="text-lg font-bold text-emerald-700">
                                {formatPrice(zone.delivery_fee)}
                              </p>
                            )}
                            {zone.whatsapp_link && (
                              <a 
                                href={zone.whatsapp_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-green-600 flex items-center gap-1 mt-1 hover:underline"
                              >
                                <MessageCircle className="w-3 h-3" />
                                WhatsApp для связи
                              </a>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditZoneDialog(zone)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => deleteZone(zone.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Info Box */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">Как работают зоны:</p>
                      <ul className="mt-1 space-y-1 text-xs">
                        <li>• Система рассчитывает расстояние от цеха до клиента (OSRM)</li>
                        <li>• Ищет зону по расстоянию (0-3 км, 3-5 км, и т.д.)</li>
                        <li>• Применяет цену соответствующей зоны</li>
                        <li>• Если расстояние > 20 км — доставка недоступна</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Preview Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Предпросмотр логики доставки</CardTitle>
            <CardDescription>Как будет работать расчёт для клиентов в {selectedCity?.name}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4">Сумма заказа</th>
                    <th className="text-left py-2 px-4">Результат</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 px-4">Менее {formatPrice(currentSettings.min_order_amount)}</td>
                    <td className="py-2 px-4 text-red-600">❌ Доставка недоступна</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-4">
                      {formatPrice(currentSettings.min_order_amount)} — {formatPrice(currentSettings.free_delivery_threshold - 1)}
                    </td>
                    <td className="py-2 px-4">
                      💰 Платная доставка по зонам
                      <div className="text-xs text-gray-500 mt-1">
                        {zones.map(z => `${z.name}: ${formatPrice(z.delivery_fee)}`).join(' | ') || 
                         `По умолчанию: ${formatPrice(currentSettings.default_delivery_fee)}`}
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-4">От {formatPrice(currentSettings.free_delivery_threshold)}</td>
                    <td className="py-2 px-4 text-emerald-600">✅ Бесплатная доставка</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4">Самовывоз (любая сумма)</td>
                    <td className="py-2 px-4 text-emerald-600">✅ Бесплатно</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Zone Dialog */}
      <Dialog open={showZoneDialog} onOpenChange={setShowZoneDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingZone ? 'Редактировать зону' : 'Добавить зону доставки'}
            </DialogTitle>
            <DialogDescription>
              Укажите расстояние и стоимость доставки
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Название зоны</Label>
              <Input
                placeholder="Например: Зона 1 (ближняя)"
                value={newZone.name}
                onChange={(e) => setNewZone({ ...newZone, name: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>От (км)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={newZone.distance_from}
                  onChange={(e) => setNewZone({ ...newZone, distance_from: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>До (км)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={newZone.distance_to}
                  onChange={(e) => setNewZone({ ...newZone, distance_to: e.target.value })}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Расстояние рассчитывается от цеха (Баранского 15) до адреса клиента по дорогам
            </p>
            
            <div className="space-y-2">
              <Label>Стоимость доставки (₸)</Label>
              <Input
                type="number"
                value={newZone.delivery_fee}
                onChange={(e) => setNewZone({ ...newZone, delivery_fee: e.target.value })}
                disabled={newZone.delivery_disabled}
              />
            </div>
            
            {/* Delivery Disabled Toggle */}
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-600" />
                  <Label className="text-red-800 font-medium">Отключить доставку</Label>
                </div>
                <Switch
                  checked={newZone.delivery_disabled}
                  onCheckedChange={(checked) => setNewZone({ ...newZone, delivery_disabled: checked })}
                />
              </div>
              <p className="text-xs text-red-600">
                Клиенты из этой зоны не смогут заказать доставку
              </p>
              
              {newZone.delivery_disabled && (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm">Сообщение для клиента</Label>
                    <Textarea
                      placeholder="Например: Доставка в пригород временно недоступна. Пожалуйста, свяжитесь с нами для уточнения."
                      value={newZone.no_delivery_message}
                      onChange={(e) => setNewZone({ ...newZone, no_delivery_message: e.target.value })}
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-green-600" />
                      Ссылка на WhatsApp
                    </Label>
                    <Input
                      placeholder="https://wa.me/77001234567"
                      value={newZone.whatsapp_link}
                      onChange={(e) => setNewZone({ ...newZone, whatsapp_link: e.target.value })}
                    />
                    <p className="text-xs text-gray-500">
                      Формат: https://wa.me/77001234567 или https://api.whatsapp.com/send?phone=77001234567
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowZoneDialog(false)}>
              Отмена
            </Button>
            <Button onClick={saveZone} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">
              {saving ? 'Сохранение...' : (editingZone ? 'Сохранить' : 'Добавить')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDeliveryZones;
